import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { useEventsController } from "../hooks/useEventsController";
import SearchBox from "../components/SearchBox";
import EventList from "../components/EventList";
import DescriptionPanel from "../components/DescriptionPanel";
import ClipPlayer from "../components/ClipPlayer";
import { ApiError } from "../api/client";
// Player-centric "Studio" view — sibling to MainView's grid. Same data
// source (useEventsController), different shape: persistent player + full
// description on the left, search + event list on the right.
//
// URL state: `?ev=<bID>` selects an event. `?from=email` flags that the
// active customer was overridden by a deep-link, which DeepLinkRedirect
// uses to show a "switched to X" banner.
const PREV_CUSTOMER_KEY = "aisearch_prev_customer";
export default function StudioView() {
    const { user, selectedCustomer, setSelectedCustomer } = useAuth();
    const [params, setParams] = useSearchParams();
    const navigate = useNavigate();
    const { events, unreachable, degraded, selected, setSelected, loading, error, searchedFor, loadDefault, search, fetchByBID, fetchByPath, availableCameras, } = useEventsController();
    const [clipError, setClipError] = useState(false);
    const [bannerOpen, setBannerOpen] = useState(false);
    const [prevCustomer, setPrevCustomer] = useState(null);
    // Tracks the ?evpath= key we've already hydrated, so the resolution effect
    // (which re-runs when events/selected change) doesn't re-fetch in a loop.
    const resolvedPath = useRef(null);
    // Resolve a customer slug to its display name via accessible_customers.
    // Falls back to the slug if the user has no record of it (e.g. lost
    // access mid-session). The banner reads better as "Switch back to
    // Rosa's Cafe #54" than "Switch back to rc54".
    const customerLabel = (slug) => {
        if (!slug)
            return "";
        const match = user?.accessible_customers.find(c => c.slug === slug);
        return match?.display_name || slug;
    };
    // Customer-switch banner: shown when the deep-link forced an override.
    // DeepLinkRedirect persists the "from" customer slug into sessionStorage
    // so we can offer a "switch back to rc54" revert.
    useEffect(() => {
        if (params.get("from") === "email") {
            const prev = sessionStorage.getItem(PREV_CUSTOMER_KEY);
            if (prev && prev !== selectedCustomer) {
                setPrevCustomer(prev);
                setBannerOpen(true);
            }
        }
    }, [params, selectedCustomer]);
    // Esc dismisses the banner (D11).
    useEffect(() => {
        if (!bannerOpen)
            return;
        const onKey = (e) => {
            if (e.key === "Escape")
                setBannerOpen(false);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [bannerOpen]);
    // Initial load: fetch recent events whenever the user / customer changes.
    useEffect(() => {
        if (user)
            loadDefault();
    }, [user, loadDefault]);
    // ?ev= URL → event resolution. Three branches:
    // 1) bID matches a row in `events` (compound match: customer + bID, since
    //    bIDs are per-shard and not globally unique) → set that row as selected
    // 2) bID is set but not in current load → fetchByBID adds it (deep-link)
    // 3) ?ev= is empty → keep events[0] selected as a sensible default
    useEffect(() => {
        if (loading)
            return;
        // Defer until events match the active customer. Without this, a customer
        // dropdown switch while ?ev= is set fires a stale fetchByBID against the
        // NEW shard before the events list refreshes (the URL-clearing effect
        // and the ev-resolution effect both fire on the same render, and the
        // ev-resolution effect sees old params + new selectedCustomer). End-state
        // is correct, but generates a wasted 503 to the new shard.
        if (events.length > 0 && events[0].customer !== selectedCustomer)
            return;
        // Durable clip-path deep-link (?evpath=<base64url(sSourcePath)>). Takes
        // precedence over ?ev=. Resolved by path, so it survives GC delete +
        // re-ingest that reassigns bIDs. We can't cheaply match it against the
        // loaded events (their sSourcePath is the served form, the key encodes
        // the raw stored form), so always hydrate — guarded by resolvedPath so
        // the effect's re-runs don't loop.
        const pathParam = params.get("evpath");
        if (pathParam) {
            if (selectedCustomer && resolvedPath.current !== pathParam) {
                resolvedPath.current = pathParam;
                fetchByPath(selectedCustomer, pathParam).catch((e) => {
                    // Same semantics as the bID branch: a 404 means the clip is truly
                    // gone (email link is stale) — drop ?evpath= and fall back to
                    // events[0]. Transient 5xx keeps the param so a refresh retries.
                    if (e instanceof ApiError && e.status === 404) {
                        resolvedPath.current = null;
                        setParams(prev => {
                            const n = new URLSearchParams(prev);
                            n.delete("evpath");
                            return n;
                        }, { replace: true });
                    }
                });
            }
            return;
        }
        const bidParam = params.get("ev");
        if (bidParam) {
            const bID = Number(bidParam);
            if (!Number.isFinite(bID))
                return;
            // Match on (customer, bID): a stale ev99 from customer A is NOT the
            // same as a fresh ev99 from customer B. Without this guard the user
            // can be silently shown an event from a previous customer's shard.
            const found = events.find(e => e.bID === bID && e.customer === selectedCustomer);
            if (found) {
                if (!selected || selected.bID !== bID || selected.customer !== selectedCustomer) {
                    setSelected(found);
                }
                return;
            }
            if (selectedCustomer
                && (!selected || selected.bID !== bID || selected.customer !== selectedCustomer)) {
                fetchByBID(selectedCustomer, bID).catch((e) => {
                    // Backend status code semantics (post-/review fix):
                    //   404 event_not_found   → bID truly missing. Drop ?ev= and
                    //                            fall back to events[0]. UX shows
                    //                            a different event with no error
                    //                            banner — that's intentional, the
                    //                            email link was bad.
                    //   503 shard_unavailable → transient. Surface as a retryable
                    //                            error banner; keep ?ev= so a refresh
                    //                            re-attempts the load.
                    //   502 shard_error       → ops issue (auth misconfig, shard bug).
                    //                            Same treatment as 503.
                    //   other (401 swallowed by hook) — also retryable.
                    if (e instanceof ApiError && e.status === 404) {
                        setParams(prev => {
                            const n = new URLSearchParams(prev);
                            n.delete("ev");
                            return n;
                        }, { replace: true });
                    }
                    // Transient errors leave ?ev= in place; the events fallback won't
                    // auto-select (because ?ev= is still set) so the player shows the
                    // "select an event" empty state. Future: surface a retryable
                    // error toast instead of silent fallback.
                });
            }
            return;
        }
        // No ?ev= specified.
        // Two reset triggers, both gated on `!selected`:
        // (a) initial mount, no selection yet → pick events[0]
        // (b) selection became stale because the events array changed (search,
        //     customer-switch, /events response replaced the load): if the
        //     prior selection isn't in `events` anymore, drop it so events[0]
        //     wins on the next pass. Without this, the player keeps the old
        //     clip while the list shows fresh results — Codex caught this.
        if (selected
            && !events.some(e => e.bID === selected.bID && e.customer === selected.customer)) {
            setSelected(null);
            return;
        }
        if (!selected && events.length > 0) {
            setSelected(events[0]);
        }
    }, [params, events, selected, selectedCustomer, loading, fetchByBID, fetchByPath, setSelected, setParams]);
    // Reset clip error whenever the selection changes.
    useEffect(() => {
        setClipError(false);
    }, [selected]);
    // setSelected wrapper that also syncs ?ev= back into the URL so the
    // current view is bookmarkable / shareable. history.replaceState would
    // bypass react-router's state; setParams keeps both in sync.
    const onSelect = useCallback((ev) => {
        setSelected(ev);
        setParams(prev => {
            const n = new URLSearchParams(prev);
            n.set("ev", String(ev.bID));
            // Once the user picks a different event, the email-origin hint + the
            // durable-path key stop being useful — drop them so a refresh reflects
            // the numeric selection and doesn't re-show the banner or re-hydrate.
            n.delete("from");
            n.delete("evpath");
            return n;
        }, { replace: true });
        resolvedPath.current = null;
    }, [setSelected, setParams]);
    // Customer-switch from the dropdown invalidates ?ev= (different shard) and
    // closes the banner (its "Switch back to X" copy would be stale relative to
    // the new selectedCustomer). useAuth.selectedCustomer is the source of truth.
    const customerOnLoad = useState(selectedCustomer)[0];
    useEffect(() => {
        if (customerOnLoad && selectedCustomer && selectedCustomer !== customerOnLoad) {
            setBannerOpen(false);
            sessionStorage.removeItem(PREV_CUSTOMER_KEY);
            setParams(prev => {
                const n = new URLSearchParams(prev);
                n.delete("ev");
                n.delete("evpath");
                n.delete("from");
                return n;
            }, { replace: true });
            resolvedPath.current = null;
        }
    }, [selectedCustomer, customerOnLoad, setParams]);
    const dismissBanner = () => {
        // Always remove the prev-customer key on dismiss so the banner doesn't
        // re-open with stale "Switch back to X" copy on a subsequent same-
        // customer email link. Without this cleanup, dismiss-X is silently
        // overridden by the next from=email URL.
        sessionStorage.removeItem(PREV_CUSTOMER_KEY);
        setBannerOpen(false);
    };
    const revertCustomer = () => {
        if (!prevCustomer)
            return;
        setSelectedCustomer(prevCustomer);
        sessionStorage.removeItem(PREV_CUSTOMER_KEY);
        setBannerOpen(false);
        navigate("/studio", { replace: true });
    };
    // Empty-state spec (D4): four distinct states, each with its own glyph + copy.
    const renderPlayer = () => {
        if (loading && !selected) {
            return (_jsx("div", { className: "player-frame empty", children: _jsxs("div", { className: "empty-state", children: [_jsx("div", { className: "icon", "aria-hidden": "true", children: "\u23F3" }), _jsx("div", { className: "copy", children: "Loading events\u2026" })] }) }));
        }
        if (!loading && events.length === 0) {
            // A shard that timed out or errored comes back in `unreachable` with
            // an empty results list — which is NOT the same as a genuine "no
            // matches." Distinguish them so a slow/offline site says "retry"
            // instead of silently reading as zero results (the exact failure mode
            // that made the brochure demo look broken — ADR-022).
            if (unreachable.length > 0) {
                return (_jsx("div", { className: "player-frame empty", children: _jsxs("div", { className: "empty-state", children: [_jsx("div", { className: "icon", "aria-hidden": "true", children: "\u26A0" }), _jsxs("div", { className: "copy", children: ["Couldn't reach ", unreachable.length === 1 ? "this site" : "some sites", " in time.", _jsx("br", {}), _jsxs("span", { className: "copy-secondary", children: ["The search timed out (", unreachable.join(", "), "). Try again."] })] })] }) }));
            }
            return (_jsx("div", { className: "player-frame empty", children: _jsxs("div", { className: "empty-state", children: [_jsx("div", { className: "icon", "aria-hidden": "true", children: "\u2205" }), _jsxs("div", { className: "copy", children: ["No events match your search.", _jsx("br", {}), _jsx("span", { className: "copy-secondary", children: "Try widening the date range or toggle \"show empty\"." })] })] }) }));
        }
        if (!selected) {
            return (_jsx("div", { className: "player-frame empty", children: _jsxs("div", { className: "empty-state", children: [_jsx("div", { className: "icon", "aria-hidden": "true", children: "\u25B6" }), _jsx("div", { className: "copy", children: "Select an event from the list to play." })] }) }));
        }
        if (clipError) {
            return (_jsx("div", { className: "player-frame empty", children: _jsxs("div", { className: "empty-state", children: [_jsx("div", { className: "icon", "aria-hidden": "true", children: "\u26A0" }), _jsxs("div", { className: "copy", children: ["Clip unavailable.", _jsx("br", {}), _jsx("span", { className: "copy-secondary", children: "The video file may have been deleted or moved." })] })] }) }));
        }
        // Normal: ClipPlayer inline mode handles the video element + autoplay;
        // we wrap in player-frame for the consistent 16:9 letterbox (D12).
        return (_jsx("div", { className: "player-frame", children: _jsx(ClipPlayer, { ev: selected, mode: "inline", onError: () => setClipError(true) }) }));
    };
    // Description panel hides entirely on the "no events" / "select to play"
    // states (description has nothing to render); shows dimmed when the clip
    // 404'd (the description text is still useful context).
    const renderDescription = () => {
        if (!selected)
            return null;
        return _jsx(DescriptionPanel, { ev: selected, dimmed: clipError });
    };
    return (_jsxs(_Fragment, { children: [bannerOpen && prevCustomer && (_jsxs("div", { className: "switch-banner", role: "alert", children: [_jsxs("span", { children: ["Switched to ", _jsx("strong", { children: customerLabel(selectedCustomer) }), " to view this event."] }), _jsxs("button", { className: "revert", onClick: revertCustomer, children: ["Switch back to ", customerLabel(prevCustomer)] }), _jsx("button", { className: "close", onClick: dismissBanner, "aria-label": "Dismiss banner", title: "Dismiss", children: "\u00D7" })] })), _jsxs("main", { className: "studio", children: [_jsxs("section", { className: "player-col", children: [renderPlayer(), renderDescription()] }), _jsxs("aside", { className: "events-col", role: "complementary", "aria-label": "Events", children: [_jsx(SearchBox, { onSearch: search, busy: loading, availableCameras: availableCameras }), error && _jsx("div", { className: "error-banner", children: error }), unreachable.length > 0 && (_jsxs("div", { className: "warning-banner", children: [unreachable.length, " site", unreachable.length === 1 ? "" : "s", " unreachable:", " ", unreachable.join(", ")] })), degraded.length > 0 && (_jsxs("div", { className: "warning-banner", "data-testid": "central-degraded", children: ["Cloud search degraded: ", degraded.map(d => d.replace("_unavailable", " search unavailable")).join(", ")] })), _jsx("div", { className: "results-status", children: searchedFor
                                    ? `Matching: ${searchedFor}`
                                    : loading ? "Loading…" : `${events.length} events` }), _jsx(EventList, { events: events, selected: selected, onSelect: onSelect, emptyText: searchedFor ? "No matches. Try widening the date range." : "No recent events." })] })] })] }));
}
